import { motion } from "framer-motion";
import "./AboutPage.css";

interface AboutPageProps {
  onClose: () => void;
}

const AboutPage = ({ onClose }: AboutPageProps) => {
  return (
    <motion.div
      className="AboutPage"
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 40 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className="AboutPage-inner">
        <button
          className="AboutPage-close"
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>

        <div className="AboutPage-sigil">✦</div>
        <h1 className="AboutPage-title">FateSpin</h1>
        <p className="AboutPage-tagline">A free tarot reading experience</p>

        <div className="AboutPage-divider" />

        <section className="AboutPage-section">
          <h2>About</h2>
          <p>
            This is a tool for reflection, not prediction. The cards reveal what
            you already know. This website is for entertainment purposes.
          </p>
        </section>

        <div className="AboutPage-divider" />

        <section className="AboutPage-section">
          <h2>Privacy Policy</h2>
          <p className="AboutPage-meta">Last updated: June 2026</p>

          <h3>What we collect</h3>
          <p>
            When you request a reading, we record the following on our servers:
          </p>
          <ul>
            <li>
              <strong>Session ID</strong> — an anonymous identifier stored on
              your device that lets us recognise returning visits without
              requiring an account
            </li>
            <li>
              <strong>IP address</strong> — used for security and rate limiting
            </li>
            <li>
              <strong>Approximate location</strong> — country, city, and region
              derived from your IP address (not GPS)
            </li>
            <li>
              <strong>Device &amp; browser</strong> — operating system, browser
              type, and language preference
            </li>
            <li>
              <strong>Reading metadata</strong> — the card drawn, whether it was
              reversed, and keywords asked
            </li>
            <li>
              <strong>Timestamp</strong> — when the reading took place
            </li>
          </ul>
          <p>
            The reading text generated by the AI is <strong>not</strong> stored.
          </p>

          <h3>Why we collect it</h3>
          <ul>
            <li>
              <strong>Rate limiting</strong> — to prevent abuse and keep the
              service available for everyone
            </li>
            <li>
              <strong>Analytics</strong> — to understand how the app is used and
              improve it
            </li>
            <li>
              <strong>Security</strong> — to detect and block malicious activity
            </li>
          </ul>

          <h3>How long we keep it</h3>
          <p>
            Reading records are retained for up to 12 months, after which they
            are deleted.
          </p>

          <h3>Who we share it with</h3>
          <p>
            Your data is never sold or shared with third parties for marketing
            purposes. We use the following services to operate FateSpin:
          </p>
          <ul>
            <li>
              <strong>Anthropic</strong> — your question and card are sent to
              Anthropic's API to generate your reading. See{" "}
              <a
                href="https://www.anthropic.com/privacy"
                target="_blank"
                rel="noreferrer"
              >
                Anthropic's Privacy Policy
              </a>
              .
            </li>
            <li>
              <strong>Vercel</strong> — our hosting and edge infrastructure. See{" "}
              <a
                href="https://vercel.com/legal/privacy-policy"
                target="_blank"
                rel="noreferrer"
              >
                Vercel's Privacy Policy
              </a>
              .
            </li>
            <li>
              <strong>Supabase</strong> — our database provider. See{" "}
              <a
                href="https://supabase.com/privacy"
                target="_blank"
                rel="noreferrer"
              >
                Supabase's Privacy Policy
              </a>
              .
            </li>
          </ul>

          <h3>Your rights</h3>
          <p>
            If you are based in the EU or UK, you have rights under GDPR
            including access, correction, and deletion of your data. To make a
            request, contact us at the address below.
          </p>

          <h3>Contact</h3>
          <p>
            Questions about this policy? Reach us at{" "}
            <a href="mailto:fatespintarot@gmail.com">fatespintarot@gmail.com</a>
            .
          </p>
        </section>
      </div>
    </motion.div>
  );
};

export default AboutPage;
